/**
 * Ant Design 5 theme for "Clinical Daylight".
 *
 * Every value here is the JS twin of a custom property in
 * src/theme/tokens.css. Restyling through this object is what lets the
 * app drop the `!important` overrides that used to fight antd's
 * defaults — if you find yourself writing `!important` against an
 * `.ant-*` class, the fix almost certainly belongs in this file.
 */

const color = {
  canvas: "#f7f9fc",
  surface: "#ffffff",
  surfaceRaised: "#fcfdfe",
  surfaceSunken: "#f1f4f9",
  // table header band — deliberately deeper than the sunken surface so
  // the column names read as a header, not as a paler first row
  tableHeader: "#dfe6f0",
  hairline: "#e3e8f0",
  hairlineStrong: "#d3dbe6",

  primary: "#1976d2",
  primaryHover: "#1565c0",
  primaryActive: "#0d47a1",
  primaryTint: "#e8f2fd",

  ink: "#0f1b2d",
  inkBody: "#33465c",
  inkMuted: "#6b7d94",
  inkFaint: "#94a3b8",

  danger: "#c02626",
  warning: "#b4530a",
  success: "#0f7b4f",
  info: "#2563eb",
};

const font = {
  body: '"Public Sans", "Segoe UI", system-ui, -apple-system, sans-serif',
  display: '"Sora", "Segoe UI", system-ui, -apple-system, sans-serif',
};

const antdTheme = {
  token: {
    colorPrimary: color.primary,
    colorInfo: color.info,
    colorSuccess: color.success,
    colorWarning: color.warning,
    colorError: color.danger,
    colorLink: color.primary,

    colorText: color.inkBody,
    colorTextHeading: color.ink,
    colorTextSecondary: color.inkMuted,
    colorTextTertiary: color.inkFaint,
    colorTextDescription: color.inkMuted,
    colorTextPlaceholder: color.inkFaint,

    colorBgLayout: color.canvas,
    colorBgContainer: color.surface,
    colorBgElevated: color.surface,
    colorBorder: color.hairlineStrong,
    colorBorderSecondary: color.hairline,
    colorFillQuaternary: color.surfaceSunken,

    fontFamily: font.body,
    fontSize: 15,
    fontSizeSM: 13.5,
    fontSizeLG: 16,
    fontSizeHeading1: 34,
    fontSizeHeading2: 26,
    fontSizeHeading3: 21,
    fontSizeHeading4: 18,
    fontSizeHeading5: 16,
    lineHeight: 1.55,

    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    controlHeight: 42,
    controlHeightSM: 34,
    controlHeightLG: 48,

    boxShadow:
      "0 1px 2px rgba(13, 27, 30, 0.05), 0 1px 3px rgba(13, 27, 30, 0.04)",
    boxShadowSecondary: "0 12px 32px rgba(13, 27, 30, 0.12)",

    motionDurationMid: "0.18s",
    wireframe: false,
  },

  components: {
    Layout: {
      bodyBg: color.canvas,
      headerBg: color.surface,
      headerHeight: 64,
      headerPadding: "0 20px",
      siderBg: color.surface,
      footerBg: color.surface,
    },

    Menu: {
      itemBg: "transparent",
      subMenuItemBg: "transparent",
      itemColor: color.inkBody,
      itemHoverColor: color.primary,
      itemHoverBg: color.surfaceSunken,
      itemSelectedColor: color.primary,
      itemSelectedBg: color.primaryTint,
      itemActiveBg: color.primaryTint,
      itemHeight: 44,
      itemMarginInline: 0,
      itemBorderRadius: 10,
      iconSize: 19,
      collapsedIconSize: 20,
      groupTitleColor: color.inkFaint,
      groupTitleFontSize: 11.5,
    },

    Table: {
      headerBg: color.tableHeader,
      headerColor: color.ink,
      headerSplitColor: "transparent",
      headerBorderRadius: 0,
      borderColor: color.hairline,
      rowHoverBg: color.surfaceSunken,
      cellPaddingBlock: 14,
      cellPaddingInline: 16,
      cellFontSize: 14.5,
      footerBg: color.surface,
      stickyScrollBarBg: color.hairlineStrong,
    },

    Button: {
      controlHeight: 40,
      controlHeightSM: 32,
      controlHeightLG: 46,
      borderRadius: 8,
      fontWeight: 500,
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
      defaultBorderColor: color.hairlineStrong,
      defaultColor: color.inkBody,
      paddingInline: 18,
    },

    Input: {
      controlHeight: 42,
      borderRadius: 8,
      activeShadow: "0 0 0 3px rgba(25, 118, 210, 0.16)",
      hoverBorderColor: color.primary,
      activeBorderColor: color.primary,
      paddingInline: 14,
    },

    InputNumber: { controlHeight: 42, borderRadius: 8 },

    Select: {
      controlHeight: 42,
      borderRadius: 8,
      optionSelectedBg: color.primaryTint,
      optionSelectedColor: color.primary,
      optionSelectedFontWeight: 600,
      optionPadding: "8px 12px",
    },

    DatePicker: {
      controlHeight: 42,
      borderRadius: 8,
      cellActiveWithRangeBg: color.primaryTint,
    },

    Modal: {
      borderRadiusLG: 16,
      headerBg: color.surface,
      contentBg: color.surface,
      titleFontSize: 18,
      titleColor: color.ink,
      padding: 0,
      paddingContentHorizontalLG: 0,
    },

    Drawer: {
      paddingLG: 0,
      colorBgElevated: color.surface,
    },

    Form: {
      labelColor: color.inkBody,
      labelFontSize: 14,
      labelRequiredMarkColor: color.danger,
      itemMarginBottom: 20,
      verticalLabelPadding: "0 0 6px",
    },

    Tag: {
      borderRadiusSM: 999,
      defaultBg: color.surfaceSunken,
      defaultColor: color.inkMuted,
      fontSizeSM: 12.5,
      lineHeightSM: 1.6,
    },

    Pagination: {
      itemActiveBg: color.primary,
      itemSize: 34,
      borderRadius: 8,
    },

    Tabs: {
      inkBarColor: color.primary,
      itemSelectedColor: color.primary,
      itemHoverColor: color.primaryHover,
      itemColor: color.inkMuted,
      titleFontSize: 15,
      horizontalItemPadding: "12px 0",
      horizontalItemGutter: 28,
    },

    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
      colorBorderSecondary: color.hairline,
    },

    Tooltip: {
      colorBgSpotlight: color.ink,
      borderRadius: 8,
      fontSize: 13.5,
    },

    Dropdown: {
      borderRadiusLG: 12,
      controlItemBgHover: color.surfaceSunken,
      paddingBlock: 8,
    },

    Switch: {
      colorPrimary: color.primary,
      colorPrimaryHover: color.primaryHover,
    },

    Skeleton: {
      gradientFromColor: "rgba(13, 27, 30, 0.05)",
      gradientToColor: "rgba(13, 27, 30, 0.02)",
      borderRadiusSM: 8,
    },

    Breadcrumb: {
      itemColor: color.inkFaint,
      lastItemColor: color.inkBody,
      linkColor: color.inkMuted,
      linkHoverColor: color.primary,
      separatorColor: color.inkFaint,
      fontSize: 13.5,
    },

    Badge: { colorError: color.danger },

    Empty: { colorTextDescription: color.inkMuted },

    Collapse: {
      headerBg: color.surfaceRaised,
      contentBg: color.surface,
      borderRadiusLG: 12,
      headerPadding: "14px 18px",
    },

    Upload: {
      colorBorder: color.hairlineStrong,
      colorFillAlter: color.surfaceRaised,
    },

    Message: { contentBg: color.surface, borderRadiusLG: 12 },

    Notification: { borderRadiusLG: 12 },
  },
};

export { color as themeColor, font as themeFont };
export default antdTheme;
